/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';

import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    ITelemetryContent,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Module
} from '@msdyn365-commerce-modules/utilities';
import { SearchBarComponent } from '@msdyn365-commerce/components';

import { IOrganizationSelectionViewProps } from './organization-selection';

interface IOrganizationSelectionModalButton {
    className: string;
    text: string;
    ariaLabel: string;
    telemetryContent: ITelemetryContent;
    onClick?(event: React.MouseEvent<HTMLElement>): void;
}

const _renderModal = (props: {
    className: string;
    isModalOpen: boolean;
    headingSection: React.ReactNode;
    bodySection: React.ReactNode;
    footerSection: React.ReactNode;
    toggleModal?: () => void;
}) => {
    const { className, isModalOpen, headingSection, bodySection, footerSection, toggleModal } = props;
    return (
        <Modal
            className='msc-modal-input-required'
            wrapClassName={className}
            autoFocus
            fade={false}
            applicationNode='renderPage'
            isOpen={isModalOpen}
            toggle={toggleModal}
        >
            <ModalHeader toggle={toggleModal}>{headingSection}</ModalHeader>
            <ModalBody>{bodySection}</ModalBody>
            <ModalFooter>{footerSection}</ModalFooter>
        </Modal>
    );
};

const ModalButton: React.FC<IOrganizationSelectionModalButton> = ({ className, text, ariaLabel, telemetryContent, onClick }) => {
    const payLoad = getPayloadObject('click', telemetryContent, text);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);
    return (
        <Button className={className} aria-label={ariaLabel} onClick={onClick} {...attributes}>
            {text}
        </Button>
    );
};

const OrganizationSelectionView: React.FC<IOrganizationSelectionViewProps> = props => {
    const {
        config: { className, doNotShowAgainCheckbox, organizationSelectionWarningDialogHeading, organizationSearchBarPlaceholder },
        context: {
            request: { telemetryPageName }
        },
        cookieConsentNotGivenErrorMessage,
        errorMessage,
        friendlyName,
        heading,
        isModalOpen,
        loaded,
        moduleClassName,
        moduleContainer,
        organizationList,
        organizationsTable,
        resources,
        skipSwitchOrganizationMessageSelected,
        telemetry,
        organizationSelectionWarningContentProps,
        onSwitchOrganizationContinued,
        searchOrganization,
        toggleModal,
        toggleSkipSwitchOrganizationMessage
    } = props;

    const {
        loadingLabel,
        modalCancelButtonAriaLabel,
        modalCancelButtonText,
        modalContinueButtonAriaLabel,
        modalContinueButtonText,
        modalSaveCheckboxLabel,
        searchBarAriaLabel,
        searchButtonAriaLabel
    } = resources;

    const modalClassName = `${moduleClassName}__modal`;
    const telemetryContent = getTelemetryObject(telemetryPageName ?? '', friendlyName, telemetry);

    if (!moduleContainer || !organizationList || (loaded && organizationList.length === 0)) {
        return null;
    }

    if (!loaded && !errorMessage?.length) {
        return <span className={`${moduleClassName}__loading`}>{loadingLabel}</span>;
    }

    if (cookieConsentNotGivenErrorMessage) {
        return (
            <Module {...moduleContainer} className={classnames(moduleContainer.className, moduleClassName, className)}>
                {heading}
                {cookieConsentNotGivenErrorMessage}
            </Module>
        );
    }

    return (
        <Module {...moduleContainer} className={classnames(moduleContainer.className, moduleClassName, className)}>
            {heading}

            <SearchBarComponent
                {...props}
                className={classnames(`${moduleClassName}__search`, 'msc-search-bar')}
                onClick={(_, searchText) => searchOrganization?.(searchText)}
                resources={{ searchBarAriaLabel, searchBarPlaceholder: organizationSearchBarPlaceholder, searchButtonAriaLabel }}
                telemetryContent={telemetryContent}
            />

            <div className={`${moduleClassName}__wrapper`}>
                {errorMessage ? <p className={`${moduleClassName}__error-message`}>{errorMessage}</p> : organizationsTable}
            </div>

            {isModalOpen &&
                _renderModal({
                    className: modalClassName,
                    isModalOpen: isModalOpen,
                    toggleModal: () => toggleModal?.('close'),
                    headingSection:
                        organizationSelectionWarningDialogHeading && organizationSelectionWarningDialogHeading.length > 0
                            ? organizationSelectionWarningDialogHeading
                            : undefined,
                    bodySection: organizationSelectionWarningContentProps && (
                        <div className={`${modalClassName}-body`}>{organizationSelectionWarningContentProps}</div>
                    ),
                    footerSection: (
                        <>
                            {!doNotShowAgainCheckbox && (
                                <div className={`${modalClassName}-checkbox-container`}>
                                    <input
                                        type='checkbox'
                                        name='save-checkbox'
                                        id='save-checkbox'
                                        checked={skipSwitchOrganizationMessageSelected}
                                        className={`${modalClassName}-checkbox`}
                                        onChange={toggleSkipSwitchOrganizationMessage}
                                    />
                                    <label htmlFor='save-checkbox' className={`${modalClassName}-checkbox-text`}>
                                        {modalSaveCheckboxLabel}
                                    </label>
                                </div>
                            )}
                            <div className={`${modalClassName}-button-container`}>
                                <ModalButton
                                    className={`${modalClassName}-cancel`}
                                    ariaLabel={modalCancelButtonAriaLabel}
                                    text={modalCancelButtonText}
                                    onClick={() => toggleModal?.('close')}
                                    telemetryContent={telemetryContent}
                                />
                                <ModalButton
                                    className={`${modalClassName}-continue`}
                                    ariaLabel={modalContinueButtonAriaLabel}
                                    text={modalContinueButtonText}
                                    onClick={onSwitchOrganizationContinued}
                                    telemetryContent={telemetryContent}
                                />
                            </div>
                        </>
                    )
                })}
        </Module>
    );
};

export default OrganizationSelectionView;
